import { CAPABILITY_IDS, capabilityValueSchema } from '@planuze/config';
import { z } from 'zod';

const CURRENCY_LEN = 3;

/* Capabilities por plano (B3 — ADR-0419): só chaves do catálogo versionado
   (`CAPABILITY_IDS`); id fora da lista é rejeitado (fail-closed). Espelha a validação
   do endpoint interno do billing-service (defesa em profundidade). */
const planCapabilitiesSchema = z
  .record(z.string(), capabilityValueSchema)
  .refine(
    (caps) => Object.keys(caps).every((k) => (CAPABILITY_IDS as readonly string[]).includes(k)),
    { message: 'unknown_capability' },
  );

/* Contratos de planos do backoffice (Epic 28 / ADR-0419/0435). Planos públicos e
   DIRECIONADOS (negociados, com alvo org/usuário) compartilham o mesmo CRUD; vivem no
   billing-service e o admin orquestra via HMAC (ADR-0222). */

/* Paginação keyset das listagens de planos (pública + negociada) — espelha
   `listCompaniesQuery` (companies.ts). Cursor opaco `(createdAt,id)`; `limit` clampado
   [1,100] (default 50). `includeArchived` (ADR-0713) só afeta a lista PÚBLICA: inclui os
   arquivados p/ o filtro Ativo/Arquivado client-side; a lista negociada o ignora. Query param
   chega como string 'true'/'false' — o admin-service repassa cru ao billing (que lê `=== 'true'`). */
export const listPlansQuery = z.object({
  includeArchived: z.enum(['true', 'false']).optional(),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListPlansQuery = z.infer<typeof listPlansQuery>;

/* Espelha o `Plan` de @planuze/billing enriquecido com os campos do `plan_catalog`
   (via `/internal/plans`). Tier é dinâmico (ADR-0418): string livre, não enum.
   `rank`/`capabilities` (ADR-0419) dirigem ordenação e gating no renderer. */
export const planDto = z.object({
  id: z.string(),
  productId: z.string(),
  lookupKey: z.string(),
  tier: z.string(),
  priceUsdCents: z.number().int(),
  interval: z.enum(['month', 'year']),
  currency: z.string(),
  displayName: z.string(),
  rank: z.number().int(),
  capabilities: z.record(z.string(), z.union([z.boolean(), z.number()])),
  /* Model B (ADR-0653) — plano de ORG: `priceUsdCents` é a BASE (inclui `includedSeats`
     assentos); assentos EXCEDENTES custam `extraSeatPriceCents` cada. 0/0 em plano pessoal. */
  includedSeats: z.number().int().nonnegative().default(0),
  extraSeatPriceCents: z.number().int().nonnegative().default(0),
  /* Visível nas listagens comuns do app (filtro do marketplace + select de plano no
     pack detail) — ADR-0508. O painel admin recebe todos os planos e alterna a flag. */
  isPublic: z.boolean(),
  /* Status do plano no catálogo (ADR-0419): `archived` = arquivado (Price inativo no Stripe,
     assinantes atuais mantêm o acesso). O painel só recebe arquivados com `includeArchived=true`
     e segmenta a listagem por este campo (filtro Ativo/Arquivado). */
  status: z.enum(['active', 'archived']),
  /* Última alteração da row (ISO) — coluna `updated_at` do plan_catalog. */
  updatedAt: z.string(),
});
export type PlanDto = z.infer<typeof planDto>;

/* Shape base do plano (sem refines cross-field) — reusado por `assignCompanyPlanBody`
   (companies.ts) via `.omit`. Não use diretamente p/ validar; use `createPlanBody`. */
export const createPlanShape = z.object({
  tier: z.string().trim().min(1),
  planKey: z.string().min(1),
  displayName: z.string().trim().min(1),
  priceCents: z.number().int().nonnegative(),
  currency: z.string().length(CURRENCY_LEN).default('usd'),
  interval: z.enum(['month', 'year']),
  rank: z.number().int().default(0),
  capabilities: planCapabilitiesSchema,
  /* Model B (ADR-0653) — plano de ORG: `priceCents` é a BASE (inclui `includedSeats`); assentos
     acima disso custam `extraSeatPriceCents` cada. Só plano de org; ausente = 0 (o billing aplica
     o default no wire). `.optional()` (não `.default`) p/ o body não exigi-los em plano pessoal. */
  includedSeats: z.number().int().nonnegative().optional(),
  extraSeatPriceCents: z.number().int().nonnegative().optional(),
  /* Planos-fonte cujas caps de CONTEÚDO do marketplace (packages/templates) este plano HERDA
     (ADR-0595). Só faz sentido em plano DIRECIONADO; fonte desconhecida é ignorada na resolução. */
  inheritedPlanKeys: z.array(z.string().min(1)).default(() => []),
  /* Visibilidade nas listagens comuns (ADR-0508); default `true` (retrocompat). */
  isPublic: z.boolean().default(true),
  /* Alvo (ADR-0435): ambos ausentes = plano público; `orgId` XOR `userId` = plano
     DIRECIONADO (negociado), com preço/capabilities próprios (não herda plano-base).
     Sem FK cross-schema (ADR-0222). */
  orgId: z.string().min(1).optional(),
  userId: z.string().min(1).optional(),
  /* Validade + motivo (ADR-0435): só p/ planos direcionados. `reason` = justificativa
     comercial auditada. Datas ISO (UTC). */
  validFrom: z.string().datetime().optional(),
  validUntil: z.string().datetime().optional(),
  reason: z.string().trim().min(10).max(500).optional(),
});

/* CRUD de plano no painel (B3 — ADR-0419; alvo/validade — ADR-0435). `createdByStaffId`
   NÃO entra no body — o admin-service injeta da sessão de staff. Base e excedente zerados não
   usam Stripe: plano público fica só-capabilities; plano de org é uma cortesia ativável. Refines
   cross-field: alvo único (org XOR user), `reason` obrigatório quando direcionado, e janela de
   validade coerente. */
export const createPlanBody = createPlanShape.superRefine((b, ctx) => {
  if (b.orgId !== undefined && b.userId !== undefined) {
    ctx.addIssue({ code: 'custom', message: 'target_conflict', path: ['userId'] });
  }
  const targeted = b.orgId !== undefined || b.userId !== undefined;
  if (targeted && b.reason === undefined) {
    ctx.addIssue({ code: 'custom', message: 'reason_required_for_targeted', path: ['reason'] });
  }
  if (
    b.validFrom !== undefined &&
    b.validUntil !== undefined &&
    Date.parse(b.validUntil) <= Date.parse(b.validFrom)
  ) {
    ctx.addIssue({ code: 'custom', message: 'invalid_validity_window', path: ['validUntil'] });
  }
});
export type CreatePlanBody = z.infer<typeof createPlanBody>;

/* PATCH edita metadata mutável do catálogo (ADR-0888). Campos `.optional()` explícitos — NUNCA
   `Schema.partial()` (anti-pattern: clobbera defaults). Nome sincroniza o Product quando existir;
   Price/intervalo/lookup key/alvo continuam imutáveis. */
export const updatePlanBody = z
  .object({
    /* Revisão vista pelo formulário: CAS obrigatório contra edição stale. */
    expectedUpdatedAt: z.string().datetime(),
    displayName: z.string().trim().min(1).optional(),
    tier: z.string().trim().min(1).optional(),
    rank: z.number().int().optional(),
    capabilities: planCapabilitiesSchema.optional(),
    /* Alterna a visibilidade nas listagens comuns (ADR-0508). */
    isPublic: z.boolean().optional(),
    /* Model B (ADR-0653): assentos inclusos são metadata do contrato. O preço extra não entra
       no PATCH porque representa um Stripe Price imutável. */
    includedSeats: z.number().int().nonnegative().optional(),
    /* Edita os planos-fonte herdados (ADR-0595); ausente = não mexe. */
    inheritedPlanKeys: z.array(z.string().min(1)).optional(),
    /* Janela + justificativa do direcionado. `null` remove uma ponta opcional da janela. */
    validFrom: z.string().datetime().nullable().optional(),
    validUntil: z.string().datetime().nullable().optional(),
    reason: z.string().trim().min(10).max(500).optional(),
  })
  .superRefine((body, ctx) => {
    if (
      body.validFrom !== undefined &&
      body.validFrom !== null &&
      body.validUntil !== undefined &&
      body.validUntil !== null &&
      Date.parse(body.validUntil) <= Date.parse(body.validFrom)
    ) {
      ctx.addIssue({ code: 'custom', message: 'invalid_validity_window', path: ['validUntil'] });
    }
  });
export type UpdatePlanBody = z.infer<typeof updatePlanBody>;

/* Plano DIRECIONADO (negociado) serializado p/ a aba Negociados (ADR-0435): uma row de
   `plan_catalog` com alvo (`orgId` XOR `userId`) + validade/motivo. Datas em ISO string. */
export const targetedPlanDto = z.object({
  id: z.string(),
  tier: z.string(),
  rank: z.number().int(),
  lookupKey: z.string(),
  displayName: z.string(),
  priceUsdCents: z.number().int(),
  currency: z.string(),
  interval: z.enum(['month', 'year']),
  capabilities: z.record(z.string(), z.union([z.boolean(), z.number()])),
  includedSeats: z.number().int().nonnegative(),
  extraSeatPriceCents: z.number().int().nonnegative(),
  /* Planos-fonte herdados (ADR-0595) — p/ o form pré-selecionar ao editar um direcionado. */
  inheritedPlanKeys: z.array(z.string()).default(() => []),
  orgId: z.string().nullable(),
  userId: z.string().nullable(),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  reason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TargetedPlanDto = z.infer<typeof targetedPlanDto>;

/* Primeiro lote seguro de planos (ADR-0820): somente arquivamento, reaproveitando a mutação
   idempotente já existente no Billing. O teto limita as chamadas Stripe sequenciais. */
/** Stripe é upstream de escrita: o teto é menor que o dos lotes puramente locais. */
export const PLAN_BULK_MAX_IDS = 10;
export const bulkArchivePlansBody = z.object({
  ids: z
    .array(z.string().min(1))
    .min(1)
    .max(PLAN_BULK_MAX_IDS)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'duplicate_bulk_ids' }),
  action: z.literal('archive'),
});
export type BulkArchivePlansBody = z.infer<typeof bulkArchivePlansBody>;

export const bulkArchivePlansResponseDto = z.object({
  results: z.array(z.object({ id: z.string(), ok: z.boolean(), error: z.string().nullable() })),
});
export type BulkArchivePlansResponse = z.infer<typeof bulkArchivePlansResponseDto>;
